"use client";

import {
  useCallback,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { flexRender, type Cell, type Row } from "@tanstack/react-table";
import type { RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import { PHONE_HIT_AREA } from "@/components/record/phone-hit-area";
import { cn } from "@/lib/utils";
import { ChevronRight } from "@/components/icons";
import { isMobileHidden, isMobilePrimary, mobileRowSlot } from "./column-meta";
import { SELECT_COLUMN_ID } from "./grid-metrics";
import { RecordRowActionSheet } from "./record-row-actions";
import { useLongPress } from "./use-long-press";
import { ownsContextMenu, useRowOpen } from "./use-row-open";
import { menuAnchorFor, rowMenuHandler } from "./use-row-menu";
import type { RowMenuController } from "./use-row-menu";
import { Inbox } from "@/components/icons";
import { EmptyState } from "@/components/ui/empty-state";
import { ListSkeleton } from "@/components/ui/skeleton-patterns";
import type { RecordObjectDef, TableAction } from "../types";
import { isAdminHref, useOpenableHref } from "@/hooks/use-openable-href";

interface RecordGridMobileListProps<TData extends RowData> {
  rows: Row<RecordGridFeatures, TData>[];
  object: RecordObjectDef<TData>;
  rowActions?: TableAction<TData>[];
  onRowClick?: (row: TData) => void;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  renderMobileRow?: (row: TData) => ReactNode;
  renderExpandedRow?: (row: TData) => ReactNode;
  isLoading?: boolean;
  isStale?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}

export function RecordGridMobileList<TData extends RowData>({
  rows,
  object,
  rowActions,
  onRowClick,
  onRowMenu,
  renderMobileRow,
  renderExpandedRow,
  isLoading,
  isStale = false,
  emptyTitle,
  emptyDescription,
  emptyAction,
}: RecordGridMobileListProps<TData>) {
  if (rows.length === 0) {
    return (
      <MobileListPlaceholder
        object={object}
        isLoading={isLoading}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
      />
    );
  }

  return (
    <div inert={isStale} className={cn(isStale && "opacity-60")}>
      {rows.map((row) => (
        <MobileListRow
          key={row.id}
          row={row}
          object={object}
          rowActions={rowActions}
          onRowClick={onRowClick}
          onRowMenu={onRowMenu}
          renderMobileRow={renderMobileRow}
          renderExpandedRow={renderExpandedRow}
        />
      ))}
    </div>
  );
}

function MobileListRow<TData extends RowData>({
  row,
  object,
  rowActions,
  onRowClick,
  onRowMenu,
  renderMobileRow,
  renderExpandedRow,
}: {
  row: Row<RecordGridFeatures, TData>;
  object: RecordObjectDef<TData>;
  rowActions?: TableAction<TData>[];
  onRowClick?: (row: TData) => void;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  renderMobileRow?: (row: TData) => ReactNode;
  renderExpandedRow?: (row: TData) => ReactNode;
}) {
  const expandedContent = renderExpandedRow?.(row.original);
  return (
    <>
      {renderMobileRow ? (
        <CustomMobileRow row={row.original} onRowMenu={onRowMenu}>
          {renderMobileRow(row.original)}
        </CustomMobileRow>
      ) : (
        <RecordGridMobileCard
          row={row}
          object={object}
          rowActions={rowActions}
          onRowClick={onRowClick}
          onRowMenu={onRowMenu}
          isExpanded={renderExpandedRow ? Boolean(expandedContent) : undefined}
        />
      )}
      {expandedContent ? (
        <div className="border-b border-border bg-muted/30 px-3 py-2">
          {expandedContent}
        </div>
      ) : null}
    </>
  );
}

function MobileListPlaceholder<TData extends RowData>({
  object,
  isLoading,
  emptyTitle,
  emptyDescription,
  emptyAction,
}: {
  object: RecordObjectDef<TData>;
  isLoading?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}) {
  return (
    <div role={isLoading ? "status" : undefined}>
      {isLoading ? (
        <>
          <span className="sr-only">Loading {object.label.toLowerCase()}</span>
          <ListSkeleton rows={5} showAction={false} />
        </>
      ) : (
        <EmptyState
          variant="compact"
          icon={object.icon ?? Inbox}
          title={emptyTitle}
          description={emptyDescription}
          className="py-12"
        >
          {emptyAction}
        </EmptyState>
      )}
    </div>
  );
}

function CustomMobileRow<TData extends RowData>({
  row,
  onRowMenu,
  children,
}: {
  row: TData;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  children: ReactNode;
}) {
  const wrapper = useRef<HTMLDivElement>(null);
  const openMenu = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const first = wrapper.current?.firstElementChild;
      const root = first instanceof HTMLElement ? first : null;
      if (!root || !onRowMenu || ownsContextMenu(event.target, root)) return;
      if (onRowMenu(row, menuAnchorFor(event, root))) event.preventDefault();
    },
    [onRowMenu, row],
  );
  return (
    <div
      ref={wrapper}
      className="contents"
      onContextMenu={onRowMenu ? openMenu : undefined}
    >
      {children}
    </div>
  );
}

interface MobileCardProps<TData extends RowData> {
  row: Row<RecordGridFeatures, TData>;
  object: RecordObjectDef<TData>;
  rowActions?: TableAction<TData>[];
  onRowClick?: (row: TData) => void;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  isExpanded?: boolean;
}

function RecordGridMobileCard<TData extends RowData>(props: MobileCardProps<TData>) {
  const rowHref = props.object.getRowHref?.(props.row.original);
  if (rowHref !== undefined && isAdminHref(rowHref)) return <GatedMobileCard {...props} href={rowHref} />;
  return <RecordGridMobileCardBody {...props} href={rowHref} />;
}

function GatedMobileCard<TData extends RowData>({ href, ...props }: MobileCardProps<TData> & { href: string }) {
  const openable = useOpenableHref(href);
  return <RecordGridMobileCardBody {...props} href={openable} />;
}

function PhoneRowCells<TData extends RowData>({
  primaryCell,
  secondaryCells,
  trailingCells,
}: {
  primaryCell: Cell<RecordGridFeatures, TData> | undefined;
  secondaryCells: Cell<RecordGridFeatures, TData>[];
  trailingCells: Cell<RecordGridFeatures, TData>[];
}) {
  return (
    <>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="min-w-0 text-base font-medium text-foreground [&>*]:max-w-full">
          {primaryCell ? renderCell(primaryCell) : null}
        </div>
        {secondaryCells.length > 0 ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground [&>*]:min-w-0 [&>*]:max-w-full">
            {secondaryCells.map((cell) => (
              <div key={cell.id} className="min-w-0 max-w-full">
                {renderCell(cell)}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      {trailingCells.length > 0 ? (
        <div className="flex max-w-[45%] shrink-0 flex-col items-end gap-0.5 text-right text-sm tabular-nums [&>*]:max-w-full">
          {trailingCells.map((cell) => (
            <div key={cell.id} className="max-w-full">
              {renderCell(cell)}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

const FALLBACK_SECONDARY_LIMIT = 4;

function phoneRowClass(isInteractive: boolean, isSelected: boolean) {
  return cn(
    "group/row flex min-h-14 select-none items-center gap-3 border-b border-border-subtle px-4 py-2.5 transition-colors [-webkit-touch-callout:none] [&_a]:pointer-events-none [&_a]:text-inherit! [&_a]:no-underline!",
    PHONE_HIT_AREA,
    isInteractive && "cursor-pointer active:bg-surface-2",
    isSelected ? "bg-selection" : "bg-surface",
  );
}

function useRowActionSheet<TData extends RowData>(actions: TableAction<TData>[] | undefined, row: TData) {
  const [open, setOpen] = useState(false);
  const hasActions = Boolean(actions?.some((action) => !action.show || action.show(row)));
  const longPress = useLongPress(hasActions ? () => setOpen(true) : undefined);
  return { open, setOpen, longPress };
}

function RecordGridMobileCardBody<TData extends RowData>({
  row,
  rowActions,
  onRowClick,
  onRowMenu,
  isExpanded,
  href,
}: MobileCardProps<TData> & { href: string | undefined }) {
  const open = useRowOpen({
    row: row.original,
    href,
    onRowClick,
    expanded: isExpanded,
  });
  const { primaryCell, secondaryCells, trailingCells } = partitionCells(row);
  const isSelected = row.getIsSelected();
  const actionSheet = useRowActionSheet(rowActions, row.original);

  return (
    <div
      data-state={isSelected ? "selected" : undefined}
      data-slot="phone-row"
      {...open.props}
      {...actionSheet.longPress}
      onContextMenu={rowMenuHandler(row.original, onRowMenu)}
      className={phoneRowClass(open.isInteractive, isSelected)}
    >
      <PhoneRowCells
        primaryCell={primaryCell}
        secondaryCells={secondaryCells}
        trailingCells={trailingCells}
      />
      {rowActions?.length ? (
        <RecordRowActionSheet
          row={row.original}
          actions={rowActions}
          open={actionSheet.open}
          onOpenChange={actionSheet.setOpen}
        />
      ) : null}
      {open.isInteractive ? (
        <ChevronRight
          className="size-4 shrink-0 text-fg-subtle"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

function partitionCells<TData extends RowData>(row: Row<RecordGridFeatures, TData>) {
  const allCells = row.getAllCells().filter(
    (cell) => cell.column.id !== SELECT_COLUMN_ID,
  );
  const isStructured = allCells.some(
    (cell) => mobileRowSlot(cell.column.columnDef.meta) !== undefined,
  );
  if (isStructured) {
    return {
      primaryCell:
        allCells.find((cell) => isMobilePrimary(cell.column.columnDef.meta)) ??
        allCells[0],
      secondaryCells: allCells.filter(
        (cell) => mobileRowSlot(cell.column.columnDef.meta) === "secondary",
      ),
      trailingCells: allCells.filter(
        (cell) => mobileRowSlot(cell.column.columnDef.meta) === "trailing",
      ),
    };
  }
  const visibleCells = row
    .getVisibleCells()
    .filter((cell) => cell.column.id !== SELECT_COLUMN_ID);
  const primaryCell =
    visibleCells.find((cell) => isMobilePrimary(cell.column.columnDef.meta)) ??
    visibleCells[0];
  return {
    primaryCell,
    secondaryCells: visibleCells
      .filter(
        (cell) =>
          cell !== primaryCell && !isMobileHidden(cell.column.columnDef.meta),
      )
      .slice(0, FALLBACK_SECONDARY_LIMIT),
    trailingCells: [],
  };
}

function renderCell<TData extends RowData>(cell: Cell<RecordGridFeatures, TData, unknown>) {
  return flexRender(cell.column.columnDef.cell, cell.getContext());
}
